import { Time } from 'lightweight-charts'
import { SettingType } from './drawing.enums'
import type { RectangleDrawingSettings } from './rectangleDrawingTool/rectangeDrawingTool'
import type { LineDrawingSettings, LineHeadType } from './lineDrawingTool/lineDrawingTool'
import type { EllipseDrawingSettings } from './ellipseDrawingTool/ellipseDrawingTool'
import type { ParallelChannelDrawingSettings } from './parallelChannelDrawingTool/parallelChannelDrawingTool'
import type { FlatTopBottomDrawingSettings } from './flatTopBottomDrawingTool/flatTopBottomDrawingTool'
import type { DisjointChannelDrawingSettings } from './disjointChannel/disjointChannel'
import type { TriangleDrawingSettings } from './triangleDrawingTool/triangleDrawingTool'
import type { CircleDrawingSettings } from './circleDrawingTool/circleDrawingTool'
import type { PathDrawingSettings } from './pathDrawingTool/pathDrawingTool'
import type { RotatedRectangleDrawingSettings } from './rotatedRectangleTool/rotatedRectangleTool'
import type { FibonacciDrawingSettings } from './fibonacciDrawingTool/fibonacciDrawingTool'
import type { TrendBasedFibonacciDrawingSettings } from './trendBasedFibonacciDrawingTool/trendBasedFibonacciDrawingTool'
import type { PositionForecastDrawingSettings } from './positionForecastDrawingTool/positionForecastDrawingTool'
import type { IndicatorLineDrawingSettings } from './indicatorLineDrawingTool/indicatorLineDrawingTool'
import type { DateRangeDrawingSettings } from './dateRangeDrawingTool/dateRangeDrawingTool'
import type Chart from '../chart'
import type { PriceRangeDrawingSettings } from './priceRangeDrawingTool/priceRangeDrawingTool'
import type { ForecastDrawingSettings } from './forecastDrawingTool/forecastDrawingTool'
import type { FibonacciCirclesDrawingSettings } from './fibonacciCirclesDrawingTool/fibonacciCirclesDrawingTool'
import type { HorizontalLineDrawingSettings } from './horizontalLineTool/horizontalLineTool'
import type { GannFanDrawingSettings } from './gannFanDrawingTool/gannFanDrawingTool'
import type { XABCDPatternDrawingSettings } from './xabcdPatternTool/xabcdPatternTool'
import type { ABCDPatternDrawingSettings } from './abcdPatternTool/abcdPatternTool'
import type { TrianglePatternDrawingSettings } from './trianglePatternTool/trianglePatternTool'
import type { OiProfileSettings } from './oiProfile/oiProfile'
import type { VerticalLineDrawingSettings } from './verticalLineTool/verticalLineTool'
import type { ThreeDivesPatternSettings } from './3DivesPatternTool/3DivesPatternTool'
import type { CrossLineDrawingSettings } from './crossLineDrawingTool/crossLineDrawingTool'
import type { ElliottImpulseWaveSettings } from './elliottImpulseWave/elliottImpulseWave'
import type { HeadAndShoulderPatternSettings } from './headAndShoulderPatternTool/headAndShoulderPatternTool'
import type { ChartPatternOverlaySettings } from './chartPatternOverlayTool/chartPatternOverlayTool'
import type { TextToolSettings } from './textTool/textTool'
import type { PriceNoteSettings } from './priceNoteTool/priceNoteTool'
import type { ContractSwitchDrawingSettings } from './contractSwitchTool/ContractSwitchTool'
import type { CorporateActionDrawingSettings } from './corporateActionTool/CorporateActionTool'
import type { OiHeatmapSettings } from './oiProfile/oiHeatmap'
import type { CompanyEventDrawingSettings } from './companyEventTool/companyEventTool.types'
import type { OrdersOnChartToolDrawingSettings } from './ordersOnChartTool/OrdersOnChartTool'
import type { OrdersMarkersDrawingSettings } from './ordersOnChartTool/OrderMarkers'
import type { BrushDrawingSettings } from './brushTool/brushTool'
import type { FixedRangeVolumeProfileDrawingSettings } from './fixedRangeVolumeProfile/fixedRangeVolumeProfile'
import type { AnchoredVolumeProfileDrawingSettings } from './AnchoredVolumeProfile/anchoredVolumeProfile'
import type { NewsArticleDrawingSettings } from './newsArticleTool/NewsArticleTool'
import type { SessionBreakDrawingSettings } from './sessionBreakTool/SessionBreakTool'
import type { ChartAlertsOnChartSettings } from './chartAlertsOnChartTool/ChartAlertsOnChartTool'

export type DrawingOptions<O extends Record<string, any>, C extends Record<string, any>, S extends Record<string, any>> = {
    id: string,
    displayType: DrawingDisplayType,
    referenceDrawingId?: string,
    chart: Chart,
    options: O,
    settings: Partial<C>,
    initialState: S,
    magnetMode: boolean,
    paneId?: string,
    seriesId?: string
    onDrawingInactive: () => void,
    onDrawingActive: (drawingType: DrawingTypes) => void,
    onDestroy: (id: string) => void,
    onDrawingComplete: (id: string) => void,
    onDrawingUpdate: (id: string) => void,
    onFocusChange: (id: string, focused: boolean) => void;
}

export type Point = {
    id: string,
    x: Time,
    y: number,
    hidden?: boolean,
    coordinates?: PointCoordinates
}

export type DrawingMode = 'DRAW' | 'EDIT';

export type LabelAxisPoints = keyof Pick<LabelAxisViewConfig<any>, 'p1' | 'p2'>

export type LabelAxisViewConfig<T extends Time | number> = {
    id: string,
    p1: T,
    p2: T,
    fill: boolean,
    fillColor: string,
    labelColors: Record<LabelAxisPoints, string>
}

export interface BitmapPositionLength {
    /** coordinate for use with a bitmap rendering scope */
    position: number;
    /** length for use with a bitmap rendering scope */
    length: number;
}

export type PointCoordinates = {
    x: number,
    y: number
}

export type DrawingSettingGroup<C, V> = {
    id: string,
    displayName: string,
    settings: DrawingSettingItem<C, V>[],
    visible: boolean
}

export type DrawingSettingItem<C, V> = {
    name: keyof C,
    label: string,
    type: SettingType,
    custom: boolean,
    visible: boolean,
    defaultValue: V
    range?: number[],
    options?: Array<{ label: string, value: V }>,
    fieldOptions?: any
}

export type ColorValue = {
    color: string,
    opacity: number
}

export type LineTypes = 'normal' | 'dotted' | 'dashed'

export type SettingValues = ColorValue | LineTypes | LineHeadType | string | boolean | number | Point

export type DrawingSettingsMap = {
    'simpleIndicatorLine': IndicatorLineDrawingSettings,
    'infoIndicatorLine': IndicatorLineDrawingSettings,
    'oiHistorical': OiHeatmapSettings,
    'oiHeatmap': OiHeatmapSettings,
    'oiProfile': OiProfileSettings,
    'trendLine': LineDrawingSettings,
    'ray': LineDrawingSettings,
    'arrow': LineDrawingSettings,
    'extendedLine': LineDrawingSettings,
    'trendAngle': LineDrawingSettings,
    'infoLine': LineDrawingSettings,
    'simpleLine': LineDrawingSettings
    'parallelChannel': ParallelChannelDrawingSettings,
    'flatTopBottom': FlatTopBottomDrawingSettings,
    'disjointChannel': DisjointChannelDrawingSettings,
    'rectangle': RectangleDrawingSettings,
    'ellipse': EllipseDrawingSettings,
    'triangle': TriangleDrawingSettings,
    'circle': CircleDrawingSettings,
    'path': PathDrawingSettings,
    'polyLine': PathDrawingSettings,
    'rotatedRectangle': RotatedRectangleDrawingSettings,
    'fibonacci': FibonacciDrawingSettings,
    'trendBasedFibonacci': TrendBasedFibonacciDrawingSettings,
    'positionForecast': PositionForecastDrawingSettings,
    'dateRange': DateRangeDrawingSettings,
    'priceRange': PriceRangeDrawingSettings,
    'forecast': ForecastDrawingSettings,
    'fibonacciCircles': FibonacciCirclesDrawingSettings,
    'horizontalLine': HorizontalLineDrawingSettings,
    'horizontalRay': HorizontalLineDrawingSettings,
    'gannFan': GannFanDrawingSettings,
    'xabcdPattern': XABCDPatternDrawingSettings,
    'abcdPattern': ABCDPatternDrawingSettings,
    'trianglePattern': TrianglePatternDrawingSettings,
    'verticalLine': VerticalLineDrawingSettings,
    'crossLine': CrossLineDrawingSettings,
    '3DivesPattern': ThreeDivesPatternSettings,
    'elliottImpulseWave': ElliottImpulseWaveSettings,
    'headAndShoulderPattern': HeadAndShoulderPatternSettings,
    'chartPatternOverlay': ChartPatternOverlaySettings,
    'text': TextToolSettings,
    'anchoredText': TextToolSettings,
    'priceNote': PriceNoteSettings,
    'zoomIn': any,
    'contractSwitch': ContractSwitchDrawingSettings
    'corporateAction': CorporateActionDrawingSettings
    'companyEvent': CompanyEventDrawingSettings,
    'chartOrdersActions': OrdersOnChartToolDrawingSettings,
    'orderMarkers': OrdersMarkersDrawingSettings,
    'newsArticle': NewsArticleDrawingSettings,
    'sessionBreak': SessionBreakDrawingSettings,
    'brush': BrushDrawingSettings,
    'replaySelection': any,
    'fixedRangeVolumeProfile': FixedRangeVolumeProfileDrawingSettings
    'anchoredVolumeProfile': AnchoredVolumeProfileDrawingSettings,
    'chartAlertsOnChart': ChartAlertsOnChartSettings
}

export type DrawingTypes = keyof DrawingSettingsMap

export type DrawingSettings<T extends DrawingTypes> = DrawingSettingsMap[T]

export enum DrawingDisplayType {
    INDICATOR = 'indicator',
    DRAWING = 'drawing',
    TOOL = 'tool'
}

export type ChartDrawingItem = {
    id: string,
    referenceDrawingId?: string,
    type: DrawingTypes,
    options: Record<string, any>,
    state: Record<string, any>,
    seriesId: string,
    paneId: string,
    displayType: DrawingDisplayType,
    settings: Record<string, any>
}

export type DrawingEvents =
    'add'
    | 'abort'
    | 'add_point'
    | 'end'
    | 'remove'
    | 'move'
    | 'focused'
    | 'update_handle'
    | 'update_settings'
    | 'sync'
    | 'unsync'
    | 'update_state'